/* ===========================================================================
 * THE WORKFORCE GROUP — Workers · Staff · Remote, and what each one MEANS.
 *
 * Declared once, here (owner decision D1, 2026-10-30). Before it, "Workers"
 * was three predicates: the roster, the attendance calendar and the reports
 * read `workforceType = SITE_WORKER`, which INCLUDES a site worker who checks
 * themselves in from their own phone; the access role's "Site workers"
 * (`SITE_WORKERS_WHERE`, api/utils/workforceScope.ts) EXCLUDES them. One word,
 * two populations — and `lib/rosterScope.ts` said outright that what each
 * segment meant was every caller's own business.
 *
 * THE RULE. The three groups split everyone cleanly — every person is in
 * exactly one of them:
 *
 *   remote   people who check themselves in with face sign-in on their own
 *            device: `attendanceProfile = SELF_ANYWHERE`. What they ARE does
 *            not matter here, so a remote site worker is Remote.
 *   workers  `workforceType = SITE_WORKER`, and NOT remote.
 *   staff    `workforceType = OFFICE_STAFF`, and NOT remote.
 *
 * So the three segments add up to the roll, and "Workers" is the access
 * role's "Site workers" exactly (the API test pins the two `where`s equal).
 *
 * EVERY ENDPOINT APPLIES IT (merge M5): the roster and its bulk selection,
 * the attendance calendar, reports, the worksite detail and the Overview's
 * movement feed through `workforceGroupWhere`
 * (packages/api/src/utils/workforceGroup.ts); every list that arrived whole
 * (People, the live map, the leave calendar) through `rowInWorkforceGroup`
 * below. Each takes it as `group=workers|staff|remote`; the older spellings
 * (`labour`, `SITE_WORKER`, `workforceType=`, `attendanceProfile=
 * SELF_ANYWHERE`) still open the same group (`workforceGroupFromParams`).
 * ======================================================================== */

import { z } from 'zod';

/** The attendance profile that makes a person Remote. */
export const REMOTE_ATTENDANCE_PROFILE = 'SELF_ANYWHERE' as const;

/** The groups, in reading order. No group = everyone. */
export const WORKFORCE_GROUPS = ['workers', 'staff', 'remote'] as const;
export type WorkforceGroup = (typeof WORKFORCE_GROUPS)[number];

/**
 * The rule as data: which `workforceType` a group requires (null = any) and
 * whether it requires the remote profile (true) or its absence (false). The
 * API builds its `where` from this table and nothing else.
 */
export const WORKFORCE_GROUP_RULE: Readonly<
  Record<WorkforceGroup, { workforceType: 'SITE_WORKER' | 'OFFICE_STAFF' | null; remote: boolean }>
> = Object.freeze({
  workers: { workforceType: 'SITE_WORKER', remote: false },
  staff: { workforceType: 'OFFICE_STAFF', remote: false },
  remote: { workforceType: null, remote: true },
});

/** The two columns the rule reads. */
export interface WorkforceGroupFacts {
  workforceType?: string | null;
  attendanceProfile?: string | null;
}

/**
 * Which group a person is in, or null when the record cannot say (no
 * workforce type and not remote) — "we don't know" is not "Staff" (§3.3).
 */
export function workforceGroupOf(person: WorkforceGroupFacts): WorkforceGroup | null {
  if (person.attendanceProfile === REMOTE_ATTENDANCE_PROFILE) return 'remote';
  if (person.workforceType === 'SITE_WORKER') return 'workers';
  if (person.workforceType === 'OFFICE_STAFF') return 'staff';
  return null;
}

/** Is this person in the group? No group narrows nothing. */
export function inWorkforceGroup(person: WorkforceGroupFacts, group: WorkforceGroup | undefined): boolean {
  return group === undefined || workforceGroupOf(person) === group;
}

/**
 * Older spellings of a group, mapped to the canonical one. Read, never written
 * — the registry's `group` entry and every server query schema share this map,
 * so `?group=labour` (the calendar's) and `?scope=SITE_WORKER` (the roster's)
 * open the same group on the screen and on the server.
 */
export const WORKFORCE_GROUP_LEGACY_VALUES: Readonly<Record<string, WorkforceGroup>> = Object.freeze({
  SITE_WORKER: 'workers',
  OFFICE_STAFF: 'staff',
  REMOTE: 'remote',
  labour: 'workers',
});

/** A raw value (canonical or legacy) as a group, or undefined when it is not one. */
export function parseWorkforceGroup(raw: unknown): WorkforceGroup | undefined {
  if (typeof raw !== 'string' || raw === '') return undefined;
  const value = WORKFORCE_GROUP_LEGACY_VALUES[raw] ?? raw;
  return (WORKFORCE_GROUPS as readonly string[]).includes(value) ? (value as WorkforceGroup) : undefined;
}

/**
 * The server's `?group=` — one field on every endpoint that offers the group.
 * A legacy spelling is accepted and canonicalised; an unknown value is a 400,
 * never a silently-ignored cut (a "Workers only" export that quietly held
 * everyone is the failure reportRange.schema.ts records).
 */
export const WorkforceGroupQuerySchema = z.preprocess(
  (v) => (typeof v === 'string' ? (WORKFORCE_GROUP_LEGACY_VALUES[v] ?? v) : v),
  z.enum(WORKFORCE_GROUPS)
);

/**
 * The group a server query asks for — `group` first, then the two older
 * params it replaced (`attendanceProfile = SELF_ANYWHERE` for Remote, and
 * `workforceType`). An older client that still sends them gets the SAME rule,
 * never the old column match: `workforceType=SITE_WORKER` now means Workers,
 * so a remote site worker is no longer in it (owner D1).
 */
export function workforceGroupFromParams(q: {
  group?: unknown;
  workforceType?: unknown;
  attendanceProfile?: unknown;
}): WorkforceGroup | undefined {
  return (
    parseWorkforceGroup(q.group) ??
    (q.attendanceProfile === REMOTE_ATTENDANCE_PROFILE ? 'remote' : undefined) ??
    parseWorkforceGroup(q.workforceType)
  );
}

/**
 * `inWorkforceGroup` for a row from an API that may predate the two columns:
 * a row carrying NEITHER is kept (an absent field narrows nothing — a filter
 * must not empty the board that says who is on site); every other row is held
 * to the rule.
 */
export function rowInWorkforceGroup(person: WorkforceGroupFacts, group: WorkforceGroup | undefined): boolean {
  if (group === undefined) return true;
  if (person.workforceType === undefined && person.attendanceProfile === undefined) return true;
  return workforceGroupOf(person) === group;
}
